import { Trans } from '@lingui/react/macro';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { motion } from 'motion/react';
import { useRef } from 'react';

import {
  ChatbotWidget,
  AgentInfoSection,
  AgentTypeToggle,
  type AgentInfoSectionRef,
  Surface,
} from '@/components';
import { FramerAnimations } from '@/components/framer-animations';
import { aiCoreClient } from '@/services/ai-core-client';

export const Route = createFileRoute('/$chatId/')({
  component: PreviewPage,
});

export function PreviewPage() {
  const { chatId } = Route.useParams();
  const agentInfoSectionRef = useRef<AgentInfoSectionRef>(null);

  const { data } = useQuery({
    queryKey: ['agentSummary', chatId],
    queryFn: () => aiCoreClient.agents.summary({ agentId: chatId }),
  });

  return (
    <div className='relative min-h-screen bg-background px-4 py-8 md:p-20 lg:grid grid-cols-2 mx-auto'>
      <div>
        <motion.div
          className='relative z-10 max-w-[600px] mx-auto'
          variants={FramerAnimations.StaggerChildren}
          initial='initial'
          animate='animate'
        >
          <motion.div
            className='text-3xl font-medium'
            variants={FramerAnimations.FadeInFromTop}
          >
            <Trans>
              Welcome to the <span className='text-violet-500'>playground</span>{' '}
              of your AI assistant!
            </Trans>
          </motion.div>

          <motion.p
            className='text-sm mt-2'
            variants={FramerAnimations.FadeInFromTop}
          >
            <Trans>I am your AI assistant,</Trans>{' '}
            <a
              href='https://kanbu.ai'
              target='_blank'
              rel='noreferrer'
              className='text-primary underline'
            >
              Kanbu.ai
            </a>
            ,{' '}
            <Trans>
              now I will show you how I can communicate with customers for you.{' '}
              <strong className='font-medium'>
                I am trained on a sample of your data and ready to try out.
              </strong>
            </Trans>
          </motion.p>

          <motion.div
            className='mt-8'
            variants={FramerAnimations.FadeInFromTop}
          >
            <Surface className='flex justify-between items-start gap-4'>
              <div className='flex flex-col gap-2'>
                <h3 className='text-sm font-medium'>
                  <Trans>E-commerce Agent</Trans>
                </h3>
                <p className='text-sm text-muted-foreground'>
                  <Trans>
                    Suitable for e-commerce. Connects to your XML feeds and
                    allows you to search, filter and compare products.
                  </Trans>
                </p>
              </div>
              <AgentTypeToggle
                chatId={chatId}
                onECommerceToggle={enabled => {
                  if (enabled) {
                    agentInfoSectionRef.current?.openFeedsAccordion();
                  }
                }}
              />
            </Surface>
          </motion.div>

          <AgentInfoSection
            ref={agentInfoSectionRef}
            summary={data}
            chatId={chatId}
          />
        </motion.div>
      </div>

      <ChatbotWidget chatId={chatId} opened />
    </div>
  );
}
